import type { ComponentConfig } from "@puckeditor/core"
import { SectionBase, sectionStyle } from "../lib/section"

export type GalleryImage = { src: string; alt: string; caption: string }

export type GalleryProps = SectionBase & {
  eyebrow: string
  title: string
  images: GalleryImage[]
  columns: 2 | 3 | 4
  /** "grid" | "masonry" */
  layout: "grid" | "masonry"
}

export const Gallery: ComponentConfig<GalleryProps> = {
  fields: {
    layout: {
      type: "radio",
      options: [
        { value: "grid", label: "Grid" },
        { value: "masonry", label: "Masonry" },
      ],
    },
    columns: {
      type: "radio",
      options: [
        { value: 2, label: "2 columns" },
        { value: 3, label: "3 columns" },
        { value: 4, label: "4 columns" },
      ],
    },
    eyebrow: { type: "text", label: "Eyebrow", contentEditable: true },
    title: { type: "textarea", contentEditable: true },
    images: {
      type: "array",
      arrayFields: {
        src: { type: "text", label: "Image URL", contentEditable: true },
        alt: { type: "text", label: "Alt text", contentEditable: true },
        caption: { type: "text", label: "Caption", contentEditable: true },
      },
      defaultItemProps: { src: "", alt: "", caption: "" },
      getItemSummary: (item) => item?.alt || item?.src || "Image",
    },
    background: {
      type: "radio",
      options: [
        { value: "none", label: "Transparent" },
        { value: "muted", label: "Muted" },
        { value: "accent", label: "Dark" },
      ],
    },
    paddingY: {
      type: "radio",
      options: [
        { value: "none", label: "None" },
        { value: "sm", label: "Small" },
        { value: "md", label: "Medium" },
        { value: "lg", label: "Large" },
      ],
    },
  },

  defaultProps: {
    layout: "grid",
    columns: 3,
    eyebrow: "",
    title: "Gallery",
    images: [],
    background: "none",
    paddingY: "lg",
  },

  render: ({ layout, columns, eyebrow, title, images, background, paddingY }) => {
    const s = sectionStyle({ background, paddingY })

    return (
      <section style={s}>
        <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 24px" }}>
          {(eyebrow || title) && (
            <div style={{ textAlign: "center", marginBottom: 40 }}>
              {eyebrow && (
                <p style={{ fontSize: 13, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--site-accent, #7c3aed)", marginBottom: 12 }}>
                  {eyebrow}
                </p>
              )}
              <h2 style={{ fontSize: "clamp(1.75rem,4vw,2.5rem)", fontWeight: 800 }}>{title}</h2>
            </div>
          )}
          {images.length > 0 && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: `repeat(${columns}, 1fr)`,
                gap: 16,
                ...(layout === "masonry" ? { gridAutoRows: "10px" } : {}),
              }}
            >
              {images.map((img, i) => (
                <figure key={i} style={{ margin: 0 }}>
                  <img
                    src={img.src}
                    alt={img.alt}
                    style={{ width: "100%", display: "block", borderRadius: 12, aspectRatio: "4/3", objectFit: "cover" }}
                  />
                  {img.caption && (
                    <figcaption style={{ marginTop: 8, fontSize: 13, color: "#6b7280", textAlign: "center" }}>{img.caption}</figcaption>
                  )}
                </figure>
              ))}
            </div>
          )}
        </div>
      </section>
    )
  },
}
